import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { BackendUserProfile, CognitoUserProfile } from '@/models/User';
import defaultLogo from '@/assets/logo/solcentre_logo.png';
import { UserRole } from '@/models/UserRole';
import solcentreApiClient from '@/http/axios';
import api from '@/http/api';
import { AppContext } from '@/models/AppContext';
import { isCognitoEnabled, isNativeCognitoEnabled } from '../auth/cognitoFeatureFlag';
import { OnboardingError, readOnboarding, type OnboardingState } from '../auth/nativeAuthApi';

// Auth token content from Cognito.
export interface AuthTokenContent {
  token: string;
  exp: number;
  email: string;
  firstName: string;
  lastName: string;
}

type ProfileHydrationStatus = 'idle' | 'loading' | 'ready' | 'error';
interface AuthLifecycleState {
  cognitoInitFailed: boolean;
  profileHydrationStatus: ProfileHydrationStatus;
  onboardingState: OnboardingState | 'idle' | 'denied' | 'sign_in_required' | 'unavailable';
}
function initialAuthLifecycle(): AuthLifecycleState {
  return {
    cognitoInitFailed: false,
    profileHydrationStatus: 'idle',
    onboardingState: 'idle',
  };
}

/** Legacy login auth token content from main-server. To be removed. */
interface MainServerComLoginAuth {
  token: string;
  exp: number;
  email: string;
  role: string[];
}

/** Legacy SolCentre login user (pre-Cognito). Response returned from v2/auth/customer-login "user" field. */
interface LegacySolcentreLoginUser {
  id: number | null;
  token: string | null;
  email: string | null;
  role: UserRole | null;
  firstName: string | null;
  lastName: string | null;
  logo: string | null | undefined;
  tc: boolean | null;
  exp: number;
  subscriptionId: string | null;
  isInvoicePaid: boolean | null;
}

export function initialLegacySolcentreUser(): LegacySolcentreLoginUser {
  return {
    id: null,
    token: null,
    email: null,
    role: null,
    firstName: null,
    lastName: null,
    logo: null,
    tc: null,
    exp: 0,
    subscriptionId: null,
    isInvoicePaid: null,
  };
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const token = ref('');
    const exp = ref(0);

    const email = ref(''); // From Legacy Auth token. To be removed. Cognito email will be stored in cognitoProfile.
    const role = ref<string[]>([]); // From Legacy Auth token. To be removed.

    const cognitoProfile = ref<CognitoUserProfile | null>(null);
    const backendProfile = ref<BackendUserProfile | null>(null);
    const legacySolcentreLoginUser = ref<LegacySolcentreLoginUser>(initialLegacySolcentreUser());

    const lifecycleStatus = ref<AuthLifecycleState>(initialAuthLifecycle());
    let profileVersion = 0;
    function setCognitoInitFailed(value: boolean) {
      lifecycleStatus.value.cognitoInitFailed = value;
    }

    /** Legacy (non-Cognito) login check by app context. */
    const isLegacyLoggedIn = computed(() => (appContext: AppContext): boolean => {
      if (appContext === AppContext.ComApp) {
        return !!token.value && Date.now() / 1000 < exp.value;
      } else {
        return (
          !!legacySolcentreLoginUser.value?.token &&
          Date.now() / 1000 < legacySolcentreLoginUser.value?.exp
        );
      }
    });
    /** Provider only — raw logo URL; null when unset. */
    const profileLogo = computed({
      get() {
        if (isCognitoEnabled) {
          const logo = backendProfile.value?.provider?.logo;
          return logo || null;
        } else {
          return legacySolcentreLoginUser.value.logo;
        }
      },
      set(logo: string | null | undefined) {
        const value = logo ?? null;
        if (isCognitoEnabled) {
          const profile = backendProfile.value;
          if (profile?.provider) {
            backendProfile.value = {
              ...profile,
              provider: { ...profile.provider, logo: value },
            };
          }
        } else {
          legacySolcentreLoginUser.value.logo = value;
        }
      },
    });

    /**
     * If cognito is enabled, checks active session based on token and exp in authStore. Do not use iodc user manager due to being async (silent renew).
     * If cognito is not enabled, checks if loggedin on SolCentre.
     *
     * Note: displayLogo is only used in SolCentre at the moment.
     */
    const displayLogo = computed(() => {
      if (isCognitoEnabled) {
        const isSessionActiveInStore = !!token.value && Date.now() / 1000 < exp.value;
        return isSessionActiveInStore
          ? backendProfile.value?.provider?.logo || defaultLogo
          : defaultLogo;
      } else {
        return isLegacyLoggedIn.value(AppContext.SolCentre)
          ? profileLogo.value || defaultLogo
          : defaultLogo;
      }
    });

    const profileFirstName = computed(() => {
      if (isCognitoEnabled) {
        return cognitoProfile.value?.firstName ?? '';
      } else {
        return legacySolcentreLoginUser.value.firstName ?? '';
      }
    });
    const profileLastName = computed(() => {
      if (isCognitoEnabled) {
        return cognitoProfile.value?.lastName ?? '';
      } else {
        return legacySolcentreLoginUser.value.lastName ?? '';
      }
    });
    const isTCRead = computed({
      get() {
        if (isCognitoEnabled) {
          return backendProfile.value?.tcAccepted ?? false;
        } else {
          return legacySolcentreLoginUser.value.tc ?? false;
        }
      },
      set(tcRead: boolean) {
        if (isCognitoEnabled) {
          if (backendProfile.value) {
            backendProfile.value = { ...backendProfile.value, tcAccepted: tcRead };
          }
        } else {
          legacySolcentreLoginUser.value.tc = tcRead;
        }
      },
    });

    const profileSubscriptionId = computed({
      get() {
        if (isCognitoEnabled) {
          const id = backendProfile.value?.provider?.subscriptionId;
          return id != null ? String(id) : null;
        } else {
          return legacySolcentreLoginUser.value.subscriptionId;
        }
      },
      set(subscriptionId: string | number | null) {
        const value =
          subscriptionId != null && subscriptionId !== '' ? String(subscriptionId) : null;
        if (isCognitoEnabled) {
          if (backendProfile.value) {
            const numericId = value != null ? Number(value) : null;
            const profile = backendProfile.value;
            if (profile?.provider) {
              backendProfile.value = {
                ...profile,
                provider: { ...profile.provider, subscriptionId: numericId },
              };
            }
          }
        } else {
          legacySolcentreLoginUser.value.subscriptionId = value;
        }
      },
    });

    const profileIsInvoicePaid = computed({
      get() {
        if (isCognitoEnabled) {
          return backendProfile.value?.provider?.isInvoicePaid ?? null;
        } else {
          return legacySolcentreLoginUser.value.isInvoicePaid;
        }
      },
      set(isInvoicePaid: boolean | null) {
        if (isCognitoEnabled) {
          if (backendProfile.value) {
            const profile = backendProfile.value;
            if (profile?.provider) {
              backendProfile.value = {
                ...profile,
                provider: { ...profile.provider, isInvoicePaid: isInvoicePaid },
              };
            }
          }
        } else {
          legacySolcentreLoginUser.value.isInvoicePaid = isInvoicePaid;
        }
      },
    });

    /** Cognito: true once backend profile hydration finished; always true for legacy auth. */
    const isProfileHydrated = computed(() => {
      if (isCognitoEnabled) {
        return lifecycleStatus.value.profileHydrationStatus === 'ready';
      }
      return true;
    });

    const profileEmail = computed(() => (appContext: AppContext): string => {
      if (isCognitoEnabled) {
        return cognitoProfile.value?.email ?? '-';
      } else {
        switch (appContext) {
          case AppContext.ComApp:
            return email.value || '-';
          case AppContext.SolCentre:
            return legacySolcentreLoginUser.value.email ?? '-';
        }
      }
    });

    /** Main-server user id for provider-scoped API calls. Legacy ComApp doesn't have this field.*/
    const profileUserId = computed(() => {
      if (isCognitoEnabled) {
        return backendProfile.value?.id ?? null;
      } else {
        return legacySolcentreLoginUser.value.id;
      }
    });

    /** Whether the user is logged in for the given app context and has the given role. */
    const isLoginAtAs = computed(
      () =>
        (appContext: AppContext, requestedRole: UserRole): boolean => {
          if (isCognitoEnabled) {
            if (lifecycleStatus.value.profileHydrationStatus !== 'ready') {
              return false;
            }
            return (
              backendProfile.value?.roles
                .map((r) => r.toLowerCase())
                .includes(requestedRole.toString().toLowerCase()) ?? false
            );
          }
          switch (appContext) {
            case AppContext.SolCentre:
              return (
                legacySolcentreLoginUser.value.role?.toLowerCase() ===
                requestedRole.toString().toLowerCase()
              );
            case AppContext.ComApp: {
              const roles = role.value?.map((r) => r.toLowerCase());
              return roles?.includes(requestedRole.toString().toLowerCase()) ?? false;
            }
          }
        },
    );

    /** Legacy (non-Cognito) bearer token for main-server requests, by app context. */
    const legacyApiToken = computed(() => (appContext: AppContext): string | null => {
      switch (appContext) {
        case AppContext.SolCentre:
          return legacySolcentreLoginUser.value.token;
        case AppContext.ComApp:
          return token.value || null;
      }
    });

    // Legacy. To be removed.
    function setMainServerComLoginAuth(mainServerLoginAuth: MainServerComLoginAuth) {
      token.value = mainServerLoginAuth.token;
      exp.value = mainServerLoginAuth.exp;
      email.value = mainServerLoginAuth.email;
      role.value = mainServerLoginAuth.role;
    }

    /** Clears legacy ComApp login fields only; does not touch SolCentre legacy user. */
    function clearMainServerComLoginAuth() {
      token.value = '';
      exp.value = 0;
      email.value = '';
      role.value = [];
    }

    /** Overwrite Cognito OIDC token and Cognito profile (no backend profile fetch). */
    function setOidcSession(userAuth: AuthTokenContent) {
      token.value = userAuth.token;
      exp.value = userAuth.exp;
      cognitoProfile.value = {
        email: userAuth.email,
        firstName: userAuth.firstName,
        lastName: userAuth.lastName,
      };
    }

    /** Re-fetch user profile from the main-server. */
    async function refreshBackendProfile(reconcile = isNativeCognitoEnabled) {
      const version = ++profileVersion;
      const accessToken = token.value;
      const current = () => !isNativeCognitoEnabled || version === profileVersion;
      lifecycleStatus.value.profileHydrationStatus = 'loading';
      try {
        if (isNativeCognitoEnabled) {
          backendProfile.value = null;
          let state = await readOnboarding(accessToken);
          if (!current()) return false;
          if (state === 'setup_required' && reconcile)
            state = await readOnboarding(accessToken, true);
          if (!current()) return false;
          lifecycleStatus.value.onboardingState = state;
          if (state !== 'ready') {
            lifecycleStatus.value.profileHydrationStatus = 'idle';
            return false;
          }
        }
        const response = isNativeCognitoEnabled
          ? await solcentreApiClient.get<BackendUserProfile>(api.usersMe, {
              // Preserve policy/session status here; the legacy interceptor returns strings.
              validateStatus: (status) =>
                (status >= 200 && status < 300) || status === 401 || status === 403,
            })
          : await solcentreApiClient.get<BackendUserProfile>(api.usersMe);
        if (!current()) return false;
        if (isNativeCognitoEnabled && [401, 403].includes(response.status))
          throw new OnboardingError(response.status);
        const { data } = response;
        backendProfile.value = data;
        lifecycleStatus.value.profileHydrationStatus = 'ready';
        return true;
      } catch (error) {
        if (!current()) return false;
        if (isNativeCognitoEnabled) {
          lifecycleStatus.value.onboardingState =
            error instanceof OnboardingError && error.status === 401
              ? 'sign_in_required'
              : error instanceof OnboardingError && error.status === 403
              ? 'denied'
              : 'unavailable';
        }
        lifecycleStatus.value.profileHydrationStatus = 'error';
        throw error;
      }
    }

    /**
     * Sets both the Cognito token and loads backend profile.
     * (ie. combination of setOidcSession and refreshBackendProfile)
     * */
    async function setAuthUser(userAuth: AuthTokenContent) {
      try {
        setOidcSession(userAuth);
        const completed = await refreshBackendProfile(isNativeCognitoEnabled);
        return !isNativeCognitoEnabled || completed;
      } catch (error) {
        console.error('setAuthUser failed:', error);
        return false;
      }
    }

    function setLegacySolcentreLoginUser(user: LegacySolcentreLoginUser) {
      legacySolcentreLoginUser.value = user;
    }

    function resetLegacySolcentreLoginUser() {
      legacySolcentreLoginUser.value = initialLegacySolcentreUser();
    }

    function setUserSubscription(subscriptionId: string | null, isInvoicePaid: boolean | null) {
      profileSubscriptionId.value = subscriptionId;
      profileIsInvoicePaid.value = isInvoicePaid;
    }

    /** Resets everything. */
    function $reset() {
      profileVersion++;
      token.value = '';
      exp.value = 0;
      email.value = ''; // Legacy. To be removed.
      role.value = []; // Legacy. To be removed.
      cognitoProfile.value = null;
      backendProfile.value = null;
      legacySolcentreLoginUser.value = initialLegacySolcentreUser();
      lifecycleStatus.value = initialAuthLifecycle();
    }

    return {
      token,
      exp,
      email, // Legacy. To be removed.
      role, // Legacy. To be removed.
      cognitoProfile,
      backendProfile,
      legacySolcentreLoginUser,
      setLegacySolcentreLoginUser,
      resetLegacySolcentreLoginUser,
      isLegacyLoggedIn,
      isLoginAtAs,
      refreshBackendProfile,
      setOidcSession,
      setAuthUser,
      setMainServerComLoginAuth, // Legacy. To be removed.
      clearMainServerComLoginAuth,
      $reset,

      // Lifecycle status:
      lifecycleStatus,
      setCognitoInitFailed,
      isProfileHydrated,

      // User Profile details:
      profileLogo,
      displayLogo,
      profileFirstName,
      profileLastName,
      isTCRead,
      profileSubscriptionId,
      profileIsInvoicePaid,
      profileUserId,
      profileEmail,
      legacyApiToken,
      setUserSubscription,
    };
  },
  {
    persist: isCognitoEnabled
      ? { pick: [], afterHydrate: ({ store }) => store.$persist() }
      : { omit: ['lifecycleStatus'] },
  },
);
